// Today's release chips (`web/src/components/bits.tsx` there), with their
// markup and words: a chip per surface an issue's work touches (Poulpe-500).
import type { ReactNode } from 'react'
import type { Released } from '../api/types'
import { APP_CHANNELS, type SurfaceRelease } from '../lib/releases'
import { cx } from '../transcript/bits'
import { CheckIcon } from './Icons'

/**
 * A surface's name as a tag. One that awaits a release stands out; a
 * released one is muted, behind a check. The name is cut with an ellipsis
 * rather than widening what holds the chip.
 */
export function SurfaceChip({
  name,
  awaited,
  title,
  children,
}: {
  name: string
  awaited: boolean
  title?: string
  children?: ReactNode
}) {
  return (
    <span className={cx('tag', 'surface-chip', awaited && 'tag-warn')} title={title}>
      {!awaited && <CheckIcon size={10} />}
      <span className="surface-chip-name">{name}</span>
      {children}
    </span>
  )
}

/**
 * The surface, and the channels that wait when only part of `app` does: `app: ios, android`; or, once a
 * version shipped it, the surface and its version: `app 0.1.2` (Poulpe-205).
 */
function releaseName({ surface, awaits }: SurfaceRelease, version?: string): string {
  const partOfApp = surface === 'app' && awaits.length > 0 && awaits.every((channel) => APP_CHANNELS.includes(channel))
    && !APP_CHANNELS.every((channel) => awaits.includes(channel))
  if (partOfApp) return `app: ${awaits.join(', ')}`
  return version && awaits.length === 0 ? `${surface} ${version}` : surface
}

/** The same in words, channel by channel, one to a line. */
function releaseTitle({ touches, awaits }: SurfaceRelease): string {
  return touches
    .map((channel) => (awaits.includes(channel) ? `Awaits its ${channel} release` : `Released on ${channel}`))
    .join('\n')
}

/**
 * One chip per surface an issue's landed work touches (`releaseSurfaces`),
 * in surface order. The hidden words say what the colour and the check
 * only repeat. Given the versions that shipped the issue, a released
 * surface's chip names its own.
 */
export function ReleaseChips({ surfaces, versions = [] }: { surfaces: readonly SurfaceRelease[]; versions?: readonly Released[] }) {
  return (
    <>
      {surfaces.map((release) => {
        const awaited = release.awaits.length > 0
        const version = versions.find((entry) => entry.surface === release.surface)?.version
        return (
          <SurfaceChip key={release.surface} name={releaseName(release, version)} awaited={awaited} title={releaseTitle(release)}>
            <span className="visually-hidden">{awaited ? ', awaiting release' : ', released'}</span>
          </SurfaceChip>
        )
      })}
    </>
  )
}
